'use client'

import { datadogRum } from '@datadog/browser-rum'
import Link from 'next/link'
import { useParams, usePathname } from 'next/navigation'
import { type ErrorInfo, useCallback, useMemo } from 'react'
import { ErrorBoundary } from 'react-error-boundary'

import { routeScope } from '@/app/route-scope'
import { Routes } from '@/app/routes'
import Icon, { IconName } from '@/components/atomic/icon'
import { BodyLarge, H2 } from '@/components/atomic/text'
import { createLogger } from '@/lib/logger'

import { ErrorBoundaryContainer, ErrorBoundaryContent } from './styles'

const LOGGER = createLogger('components/layout/with-error-boundary')

enum ErrorDomain {
  Page = 'page',
  Account = 'account',
  Model = 'model',
  Bond = 'bond',
}

const ErrorFallback = ({
  errorDomain,
  isDetailPage,
}: {
  errorDomain: ErrorDomain
  isDetailPage: boolean
}) => {
  const listPage = useMemo(
    () => getListPageFromErrorDomain(errorDomain),
    [errorDomain],
  )
  const viewOtherText = useMemo(() => {
    return `view other ${isDetailPage ? `${errorDomain}s` : 'pages'}`
  }, [isDetailPage, errorDomain])
  return (
    <ErrorBoundaryContainer>
      <ErrorBoundaryContent>
        <Icon
          iconName={IconName.ExclamationTriangleIcon}
          className="text-moment-orange-700 dark:text-moment-orange-500"
          size="x-large"
        />
        <H2 className="text-primary">
          {`Uh oh, something went wrong ${isDetailPage ? `for this ${errorDomain}` : ''}`}
        </H2>
        <BodyLarge className="text-secondary">
          {`Looks like we ran into some unexpected error while loading this ${isDetailPage ? `${errorDomain}` : 'page'}. Our team has been notified.`}
          <br />
          <br />
          {`While we fix this, you can still navigate to ${!listPage ? viewOtherText : ''}`}
          {listPage && (
            <Link href={listPage} className="underline">
              {viewOtherText}
            </Link>
          )}
          {`. If there are any urgent requests, please contact our team via the chat bubble in the bottom left.`}
        </BodyLarge>
      </ErrorBoundaryContent>
    </ErrorBoundaryContainer>
  )
}

/**
 * Check whether an error is a Next.js internal error (e.g. notFound(),
 * redirect()) that should propagate to the framework's own error handling
 * rather than being swallowed by our error boundary.
 */
export function isNextNavigationError(error: Error): boolean {
  const digest =
    'digest' in error && typeof error.digest === 'string'
      ? error.digest
      : undefined

  if (!digest) return false

  return (
    digest.startsWith('NEXT_HTTP_ERROR_FALLBACK;') ||
    digest.startsWith('NEXT_REDIRECT;')
  )
}

function onError(error: Error, info: ErrorInfo, pathname: string) {
  // Next.js navigation errors (notFound, redirect) will still be re-thrown
  // from the fallback render — skip logging them as real errors.
  if (isNextNavigationError(error)) return

  const digest =
    'digest' in error && typeof error.digest === 'string'
      ? error.digest
      : undefined

  // The error itself carries name/message/stack — the logger forwards it as
  // the structured error field, so only the React context rides alongside.
  LOGGER.error('Error caught by error boundary', {
    error,
    componentStack: info.componentStack,
    pathname,
    digest,
    timestamp: new Date().toISOString(),
  })

  // Also send to Datadog RUM for better error tracking
  datadogRum.addError(error, {
    source: 'error-boundary',
    context: {
      pathname,
      componentStack: info.componentStack,
      digest,
    },
  })
}

function getErrorDomainFromRoute(pathname: string, isDetailPage: boolean) {
  const route = pathname.split('/')[1]
  if (isDetailPage) {
    if (route === 'accounts') {
      return ErrorDomain.Account
    }
    if (route === 'models') {
      return ErrorDomain.Model
    }
    if (route === 'research') {
      return ErrorDomain.Bond
    }
  }
  return ErrorDomain.Page
}

function getListPageFromErrorDomain(errorDomain: ErrorDomain): string | null {
  if (errorDomain === ErrorDomain.Account) {
    return `${Routes.Views.root}?entity=account`
  }
  if (errorDomain === ErrorDomain.Model) {
    return Routes.Models.list
  }
  if (errorDomain === ErrorDomain.Bond) {
    return Routes.Research.fixedIncome
  }
  return null
}

/**
 * This component is used to wrap any supplied children in an error boundary.
 * It will catch any errors that occur within the application and display an error fallback.
 * It will also log the error to the console and send it to Datadog.
 *
 * This is used in the root layout to provide error boundary coverage for all routes.
 * It can also be used to wrap individual components when more granular error handling is needed.
 *
 * @param children - The children to wrap in an error boundary.
 * @returns A React component that wraps the supplied children in an error boundary.
 */

// biome-ignore lint/style/noDefaultExport: Existing default export — prefer named exports for new code
export default function WithErrorBoundary({
  children,
}: {
  children: React.ReactNode
}) {
  const pathname = usePathname()
  const params = useParams<{ id?: string }>()

  const isDetailPage = useMemo(
    () => params.id !== undefined && params.id !== '',
    [params.id],
  )

  const errorDomain = useMemo(
    () => getErrorDomainFromRoute(pathname, isDetailPage),
    [pathname, isDetailPage],
  )

  const handleError = useCallback(
    (error: Error, info: ErrorInfo) => {
      onError(error, info, pathname)
    },
    [pathname],
  )

  const fallbackRender = useCallback(
    ({ error }: { error: Error }) => {
      // Re-throw Next.js navigation errors (notFound, redirect) so the
      // framework handles them instead of showing our generic error UI.
      if (isNextNavigationError(error)) {
        throw error
      }

      return (
        <ErrorFallback errorDomain={errorDomain} isDetailPage={isDetailPage} />
      )
    },
    [errorDomain, isDetailPage],
  )

  // Each navigation remounts the tree below, except between paths that share
  // a scope, like a new agent chat and the run it names: remounting there ends
  // the chat's live voice call. `resetKeys` still clears an error between them.
  return (
    <ErrorBoundary
      key={routeScope(pathname)}
      resetKeys={[pathname]}
      fallbackRender={fallbackRender}
      onError={handleError}
    >
      {children}
    </ErrorBoundary>
  )
}
